import { useState, useEffect, useCallback, useRef } from 'react';
import { AIChatState, ChatMessage, CreateSessionRequest, SendMessageRequest } from '@/types/aiChat';
import { aiChatApi, aiChatSessionManager } from '@/services/aiChatApi';

// 初始状态
const initialState: AIChatState = {
  isOpen: false,
  isMinimized: false,
  session: null,
  messages: [],
  isLoading: false,
  error: null,
  isTyping: false,
  unreadCount: 0,
  streamingContent: '',
  isStreaming: false
};

export const useAIChat = () => {
  const [state, setState] = useState<AIChatState>(initialState);
  const typingTimeoutRef = useRef<NodeJS.Timeout>();
  const messageIdRef = useRef(0);

  // 生成唯一消息ID
  const generateMessageId = () => {
    messageIdRef.current += 1;
    return `msg_${Date.now()}_${messageIdRef.current}`;
  };

  // 更新状态的辅助函数
  const updateState = useCallback((updates: Partial<AIChatState>) => {
    setState(prev => ({ ...prev, ...updates }));
  }, []);

  // 创建会话
  const createSession = useCallback(async () => {
    try {
      console.log('[AI Chat] 开始创建会话...');
      updateState({ isLoading: true, error: null });

      const request: CreateSessionRequest = {
        session_type: 'anonymous', // 目前支持匿名会话
        context: {
          source: 'web_widget',
          timestamp: new Date().toISOString()
        }
      };

      console.log('[AI Chat] 发送创建会话请求:', request);
      const response = await aiChatApi.createSession(request);
      console.log('[AI Chat] 会话创建响应:', response);

      // 保存会话信息
      aiChatSessionManager.saveSession(response);

      // 创建会话成功后的欢迎消息
      const welcomeMessage: ChatMessage = {
        id: generateMessageId(),
        message_type: 'assistant',
        content: response.welcome_message,
        created_at: new Date().toISOString()
      };

      const sessionData = {
        id: response.session_id,
        session_token: response.session_token,
        session_type: 'anonymous' as const,
        status: 'active' as const,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        expires_at: response.expires_at
      };

      console.log('[AI Chat] 更新状态 - 会话数据:', sessionData);
      console.log('[AI Chat] 欢迎消息:', welcomeMessage);

      updateState({
        session: sessionData,
        messages: [welcomeMessage],
        isLoading: false
      });

      console.log('[AI Chat] 会话创建完成');

    } catch (error: any) {
      console.error('[AI Chat] 会话创建失败:', error);
      console.error('[AI Chat] 错误详情:', {
        message: error.message,
        stack: error.stack,
        name: error.name
      });
      updateState({
        isLoading: false,
        error: error.message || '连接客服失败，请稍后重试'
      });
    }
  }, [updateState]);

  // 恢复已有会话
  const restoreSession = useCallback(async () => {
    const savedSession = aiChatSessionManager.getSession();
    if (!savedSession) return false;

    try {
      updateState({ isLoading: true });

      // 恢复会话信息
      const session = {
        id: savedSession.session_id,
        session_token: savedSession.session_token,
        session_type: 'anonymous' as const,
        status: 'active' as const,
        created_at: savedSession.created_at,
        updated_at: new Date().toISOString(),
        expires_at: savedSession.expires_at
      };

      // 恢复消息历史
      const savedMessages = aiChatSessionManager.getMessages();
      const messages: ChatMessage[] = savedMessages.map(msg => ({
        id: msg.message_id,
        message_type: msg.message_type as 'user' | 'assistant',
        content: msg.content,
        created_at: msg.created_at,
        processing_time_ms: msg.processing_time_ms,
        sources: msg.sources
      }));

      updateState({
        session,
        messages,
        isLoading: false
      });

      return true;
    } catch (error) {
      console.error('Restore session failed:', error);
      // 如果恢复失败，清除本地数据
      aiChatSessionManager.clearSession();
      updateState({ isLoading: false });
      return false;
    }
  }, [updateState]);

  // 发送消息
  const sendMessage = useCallback(async (content: string) => {
    if (!state.session || state.isLoading) return;

    const userMessage: ChatMessage = {
      id: generateMessageId(),
      message_type: 'user',
      content,
      created_at: new Date().toISOString()
    };

    // 创建临时的助手消息用于显示流式内容
    const tempAssistantMessage: ChatMessage = {
      id: generateMessageId(),
      message_type: 'assistant',
      content: '',
      created_at: new Date().toISOString()
    };

    // 立即添加用户消息到界面
    updateState({
      messages: [...state.messages, userMessage, tempAssistantMessage],
      isLoading: true,
      isTyping: true,
      error: null,
      streamingContent: '',
      isStreaming: true
    });

    try {
      const request: SendMessageRequest = {
        session_id: state.session.id,
        content,
        message_type: 'user'
      };

      // 使用流式API
      await aiChatApi.sendMessageStream(request, {
        onContent: (contentChunk: string) => {
          setState(prev => {
            const newContent = prev.streamingContent + contentChunk;
            const updatedMessages = prev.messages.map((msg: any, index: number) =>
              index === prev.messages.length - 1
                ? { ...msg, content: newContent }
                : msg
            );
            return {
              ...prev,
              streamingContent: newContent,
              messages: updatedMessages
            };
          });
        },
        onStats: (stats: any) => {
          console.log('流式响应统计:', stats);
        },
        onError: (error: string) => {
          updateState({
            isLoading: false,
            isTyping: false,
            isStreaming: false,
            streamingContent: '',
            error: error || '发送消息失败，请稍后重试'
          });
        },
        onComplete: () => {
          // 清除输入中状态
          if (typingTimeoutRef.current) {
            clearTimeout(typingTimeoutRef.current);
          }

          // 更新最终状态
          setState((prev: any) => {
            const finalContent = prev.streamingContent;
            const finalMessages = prev.messages.map((msg: any, index: number) =>
              index === prev.messages.length - 1
                ? {
                    ...msg,
                    content: finalContent,
                    processing_time_ms: 0 // 会在后端保存时更新
                  }
                : msg
            );

            // 保存最终消息到本地（简化版本，实际应该从后端获取完整消息数据）
            const finalMessage = {
              message_id: tempAssistantMessage.id,
              content: finalContent,
              message_type: 'assistant' as const,
              created_at: new Date().toISOString(),
              processing_time_ms: 0,
              sources: undefined
            };
            aiChatSessionManager.addMessage(finalMessage);

            return {
              ...prev,
              messages: finalMessages,
              isLoading: false,
              isTyping: false,
              isStreaming: false,
              streamingContent: '',
              unreadCount: prev.isOpen && !prev.isMinimized ? 0 : prev.unreadCount + 1
            };
          });
        }
      });

    } catch (error: any) {
      console.error('Send message failed:', error);

      // 清除输入中状态
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }

      updateState({
        isLoading: false,
        isTyping: false,
        isStreaming: false,
        streamingContent: '',
        error: error.message || '发送消息失败，请稍后重试'
      });
    }
  }, [state.session, state.messages, state.isLoading, state.isOpen, state.isMinimized, state.unreadCount, state.streamingContent, updateState]);

  // 打开聊天窗口
  const openChat = useCallback(async () => {
    console.log('[AI Chat] 打开聊天窗口');
    console.log('[AI Chat] 当前状态:', { hasSession: !!state.session, isLoading: state.isLoading });

    updateState({ isOpen: true, isMinimized: false });

    // 如果没有会话，先尝试恢复，失败则创建新会话
    if (!state.session) {
      console.log('[AI Chat] 没有现有会话，尝试恢复会话...');
      const restored = await restoreSession();
      if (!restored) {
        console.log('[AI Chat] 会话恢复失败，创建新会话...');
        await createSession();
      } else {
        console.log('[AI Chat] 会话恢复成功');
      }
    } else {
      console.log('[AI Chat] 使用现有会话');
    }
  }, [state.session, restoreSession, createSession, updateState]);

  // 关闭聊天窗口
  const closeChat = useCallback(() => {
    updateState({ isOpen: false, isMinimized: false, unreadCount: 0 });
  }, [updateState]);

  // 最小化聊天窗口
  const minimizeChat = useCallback(() => {
    updateState({ isMinimized: true });
  }, [updateState]);

  // 最大化聊天窗口
  const maximizeChat = useCallback(() => {
    updateState({ isMinimized: false, unreadCount: 0 });
  }, [updateState]);

  // 清除错误
  const clearError = useCallback(() => {
    updateState({ error: null });
  }, [updateState]);

  // 标记为已读
  const markAsRead = useCallback(() => {
    updateState({ unreadCount: 0 });
  }, [updateState]);

  // 关闭会话
  const closeSession = useCallback(async () => {
    if (state.session) {
      try {
        await aiChatApi.closeSession(state.session.id);
      } catch (error) {
        console.error('Close session failed:', error);
      }
    }

    // 清除本地数据
    aiChatSessionManager.clearSession();
    updateState({
      session: null,
      messages: [],
      isLoading: false,
      error: null,
      isTyping: false
    });
  }, [state.session, updateState]);

  // 页面卸载时自动关闭会话
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (state.session) {
        // 使用navigator.sendBeacon确保在页面卸载时能发送请求
        const closeData = JSON.stringify({});
        navigator.sendBeacon(
          `${import.meta.env.VITE_API_URL || 'http://localhost:8080'}/api/v1/ai-chat/sessions/${state.session.id}/close`,
          closeData
        );
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [state.session]);

  // 清理定时器
  useEffect(() => {
    return () => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
    };
  }, []);

  return {
    state,
    actions: {
      openChat,
      closeChat,
      minimizeChat,
      maximizeChat,
      sendMessage,
      clearError,
      markAsRead,
      closeSession,
      createSession,
      restoreSession
    }
  };
};